/**
 * @file Stubs for metrics backend data.
 *
 * The items in this file mimics the way that the backend returns the metrics.
 * This should allow for an easy switch between selected metrics and static
 * metrics while flex metrics is in development.
 *
 * When flex metrics is GA'd, this file (and any references to the data) should be removed.
 */
import { MetricDefinition, MetricDefinitionsBySendAction } from 'modules/workflows/types/metrics';
import { CAMEL_CASED_CLASS_NODE_TYPE_MAP, NODE_TYPES } from '../nodes';
import { MetricCategory, MetricValueType, SendMetric } from './enums';

const createMetric = (
  metricName: string,
  metricNiceName: string,
  valueType: MetricValueType,
  category: MetricCategory,
): MetricDefinition => ({ metricName, metricNiceName, valueType, category });

/**
 * Metric items for each send node type.
 */
export const METRIC_DEFINITIONS_STUB = {
  [SendMetric.EmailClickRate]: createMetric(
    SendMetric.EmailClickRate,
    'Email Click Rate',
    MetricValueType.Percentage,
    MetricCategory.Engagement,
  ),
  [SendMetric.EmailDeliveryRate]: createMetric(
    SendMetric.EmailDeliveryRate,
    'Email Delivery Rate',
    MetricValueType.Percentage,
    MetricCategory.Delivery,
  ),
  [SendMetric.EmailOpenRate]: createMetric(
    SendMetric.EmailOpenRate,
    'Email Open Rate',
    MetricValueType.Percentage,
    MetricCategory.Engagement,
  ),
  [SendMetric.TotalEmailSend]: createMetric(
    SendMetric.TotalEmailSend,
    'Total Email Sends',
    MetricValueType.Number,
    MetricCategory.Audience,
  ),
  [SendMetric.TotalInAppSend]: createMetric(
    SendMetric.TotalInAppSend,
    'Total In-app Sent',
    MetricValueType.Number,
    MetricCategory.Audience,
  ),
  [SendMetric.TotalInAppClick]: createMetric(
    SendMetric.TotalInAppClick,
    'Total In-app Clicks',
    MetricValueType.Number,
    MetricCategory.Engagement,
  ),
  [SendMetric.PushOpenRate]: createMetric(
    SendMetric.PushOpenRate,
    'Push Open Rate',
    MetricValueType.Percentage,
    MetricCategory.Engagement,
  ),
  [SendMetric.TotalPushSend]: createMetric(
    SendMetric.TotalPushSend,
    'Total Pushes Sent',
    MetricValueType.Number,
    MetricCategory.Audience,
  ),
  [SendMetric.PushDeliveryRate]: createMetric(
    SendMetric.PushDeliveryRate,
    'Push Delivery Rate',
    MetricValueType.Percentage,
    MetricCategory.Delivery,
  ),
  [SendMetric.TotalSMSSend]: createMetric(
    SendMetric.TotalSMSSend,
    'Total SMS Sent',
    MetricValueType.Number,
    MetricCategory.Audience,
  ),
  [SendMetric.SMSDeliveryRate]: createMetric(
    SendMetric.SMSDeliveryRate,
    'SMS Delivery Rate',
    MetricValueType.Percentage,
    MetricCategory.Delivery,
  ),
  [SendMetric.TotalWebPushClick]: createMetric(
    SendMetric.TotalWebPushClick,
    'Total Web Push Clicks',
    MetricValueType.Number,
    MetricCategory.Engagement,
  ),
  [SendMetric.TotalWebPushSend]: createMetric(
    SendMetric.TotalWebPushSend,
    'Total Web Pushes Sent',
    MetricValueType.Number,
    MetricCategory.Audience,
  ),
  [SendMetric.TotalEmbeddedRetrieved]: createMetric(
    SendMetric.TotalEmbeddedRetrieved,
    'Total Embedded Retrieved',
    MetricValueType.Number,
    MetricCategory.Audience,
  ),
  [SendMetric.EmbeddedImpressionsRate]: createMetric(
    SendMetric.EmbeddedImpressionsRate,
    'Embedded Impressions Rate',
    MetricValueType.Percentage,
    MetricCategory.Audience,
  ),
  [SendMetric.EmbeddedClickRate]: createMetric(
    SendMetric.EmbeddedClickRate,
    'Embedded Click Rate',
    MetricValueType.Percentage,
    MetricCategory.Engagement,
  ),
  [SendMetric.TotalWhatsAppSend]: createMetric(
    SendMetric.TotalWhatsAppSend,
    'Total WhatsApp Sent',
    MetricValueType.Number,
    MetricCategory.Audience,
  ),
  [SendMetric.WhatsAppDeliveryRate]: createMetric(
    SendMetric.WhatsAppDeliveryRate,
    'WhatsApp Delivery Rate',
    MetricValueType.Percentage,
    MetricCategory.Delivery,
  ),
  [SendMetric.WhatsAppClickRate]: createMetric(
    SendMetric.WhatsAppClickRate,
    'WhatsApp Click Rate',
    MetricValueType.Percentage,
    MetricCategory.Engagement,
  ),
};

/**
 * Metric items for each send action type.
 * This mimics `tileMetrics` in the workflow graphql definition.
 */
export const TILE_METRICS_STUB: MetricDefinitionsBySendAction = {
  [CAMEL_CASED_CLASS_NODE_TYPE_MAP[NODE_TYPES.SEND_EMAIL]]: [
    METRIC_DEFINITIONS_STUB[SendMetric.TotalEmailSend],
    METRIC_DEFINITIONS_STUB[SendMetric.EmailDeliveryRate],
    METRIC_DEFINITIONS_STUB[SendMetric.EmailOpenRate],
    METRIC_DEFINITIONS_STUB[SendMetric.EmailClickRate],
  ],
  [CAMEL_CASED_CLASS_NODE_TYPE_MAP[NODE_TYPES.SEND_PUSH]]: [
    METRIC_DEFINITIONS_STUB[SendMetric.TotalPushSend],
    METRIC_DEFINITIONS_STUB[SendMetric.PushDeliveryRate],
    METRIC_DEFINITIONS_STUB[SendMetric.PushOpenRate],
  ],
  [CAMEL_CASED_CLASS_NODE_TYPE_MAP[NODE_TYPES.SEND_SMS]]: [
    METRIC_DEFINITIONS_STUB[SendMetric.TotalSMSSend],
    METRIC_DEFINITIONS_STUB[SendMetric.SMSDeliveryRate],
  ],
  [CAMEL_CASED_CLASS_NODE_TYPE_MAP[NODE_TYPES.SEND_WEB_PUSH]]: [
    METRIC_DEFINITIONS_STUB[SendMetric.TotalWebPushSend],
    METRIC_DEFINITIONS_STUB[SendMetric.TotalWebPushClick],
  ],
  [CAMEL_CASED_CLASS_NODE_TYPE_MAP[NODE_TYPES.SEND_IN_APP]]: [
    METRIC_DEFINITIONS_STUB[SendMetric.TotalInAppSend],
    METRIC_DEFINITIONS_STUB[SendMetric.TotalInAppClick],
  ],
  [CAMEL_CASED_CLASS_NODE_TYPE_MAP[NODE_TYPES.SEND_EMBEDDED]]: [
    METRIC_DEFINITIONS_STUB[SendMetric.TotalEmbeddedRetrieved],
    METRIC_DEFINITIONS_STUB[SendMetric.EmbeddedImpressionsRate],
    METRIC_DEFINITIONS_STUB[SendMetric.EmbeddedClickRate],
  ],
  [CAMEL_CASED_CLASS_NODE_TYPE_MAP[NODE_TYPES.SEND_WHATSAPP]]: [
    METRIC_DEFINITIONS_STUB[SendMetric.TotalWhatsAppSend],
    METRIC_DEFINITIONS_STUB[SendMetric.WhatsAppDeliveryRate],
    METRIC_DEFINITIONS_STUB[SendMetric.WhatsAppClickRate],
  ],
};
